import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { Ticket, Trophy, Archive, BadgeCheck } from 'lucide-react';
import { LiveRunnerList } from '@/features/races/detail/LiveRunnerList';
import { LiveLeaderboardWidget } from '@/features/races/detail/LiveLeaderboardWidget';
import { cn } from '@/lib/utils';
import { isRaceLive, isRacing as checkIsRacing } from '@/lib/live';
import { Role, RaceStatus } from '@/gql/graphql';
import { Page } from '@/components/layout/Page';
import { PageHeader } from '@/components/layout/PageHeader';
import { PageContent } from '@/components/layout/PageContent';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/components/ui/Toast';
import { ErrorBanner } from '@/components/ui/ErrorBanner';
import { ConfirmModal } from '@/components/ui/ConfirmModal';
import { GET_RACE_DETAILS, MY_BETS } from '@/graphql/queries';
import { DELETE_RUNNER, DELETE_RACE, UPDATE_RACE, SET_RACE_OFFICIAL } from '@/graphql/mutations';
import { RaceDetailsSkeleton } from '@/components/skeletons/RaceDetailsSkeleton';
import { RaceInfoCard } from '@/features/races/detail/RaceInfoCard';
import { RaceDescription } from '@/features/races/detail/RaceDescription';
import { AdminMenu } from '@/features/races/detail/AdminMenu';
import { RunnerFilterBar } from '@/features/races/detail/RunnerFilterBar';
import { RunnerList } from '@/features/races/detail/RunnerList';
import { DeleteRunnerModal } from '@/features/races/detail/DeleteRunnerModal';
import { DeleteRaceModal } from '@/features/races/detail/DeleteRaceModal';
import { SyncUtmbRunnersModal } from '@/features/races/detail/SyncUtmbRunnersModal';
import { SyncUtmbResultsModal } from '@/features/races/detail/SyncUtmbResultsModal';
import { BettingActivityCard } from '@/features/races/detail/BettingActivityCard';
import { ParticipantsCard } from '@/features/races/detail/ParticipantsCard';
import { LeaderboardPreview } from '@/features/races/detail/LeaderboardPreview';
import type { GetRaceDetailsQuery } from '@/gql/graphql';

type RaceRunner = NonNullable<NonNullable<GetRaceDetailsQuery['race']>['runners']>[number];

export function RaceDetails() {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const { t } = useTranslation('races');
    const { t: tc } = useTranslation();
    const [searchTerm, setSearchTerm] = useState('');
    const [genderFilter, setGenderFilter] = useState<'ALL' | 'MALE' | 'FEMALE'>('ALL');
    const [myBetsOnly, setMyBetsOnly] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [runnerToDelete, setRunnerToDelete] = useState<RaceRunner | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showArchiveModal, setShowArchiveModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showOfficialModal, setShowOfficialModal] = useState(false);
    const [showSyncModal, setShowSyncModal] = useState(false);
    const [showSyncResultsModal, setShowSyncResultsModal] = useState(false);
    const [isDeletingRace, setIsDeletingRace] = useState(false);
    const [showRaceImage, setShowRaceImage] = useState(true);

    const { user: currentUser } = useAuth();
    const { toast } = useToast();
    const { data, loading, error, refetch } = useQuery(GET_RACE_DETAILS, {
        variables: { id: id! },
        skip: !id,
    });

    const [deleteRunner] = useMutation(DELETE_RUNNER, {
        refetchQueries: ['GetRaceDetails'],
    });

    const [updateRace, { loading: archiving }] = useMutation(UPDATE_RACE);
    const [setRaceOfficial, { loading: togglingOfficial }] = useMutation(SET_RACE_OFFICIAL, {
        refetchQueries: ['GetRaceDetails'],
    });
    const [deleteRace] = useMutation(DELETE_RACE, {
        update(cache) {
            cache.evict({ id: cache.identify({ __typename: 'Race', id: id! }) });
            cache.gc();
        },
    });

    const race = data?.race;
    const runners = race?.runners;

    // Fetch user's bet for this race (for sorting runners by bet order)
    const { data: betData } = useQuery(MY_BETS, {
        variables: { pagination: { page: 1, limit: 1 }, filter: { raceId: id } },
        skip: !id || !race?.hasCurrentUserBet,
    });
    const userBetRankings = useMemo(() => {
        const rankings = betData?.myBets?.items?.[0]?.rankings;
        if (!rankings) return null;
        const map = new Map<string, number>();
        for (const r of rankings) {
            if (r.runner?.id && r.position != null) {
                map.set(r.runner.id, r.position);
            }
        }
        return map.size > 0 ? map : null;
    }, [betData]);

    // Bet scores per runner (for closed/archived display)
    const userBetScores = useMemo(() => {
        const rankings = betData?.myBets?.items?.[0]?.rankings;
        if (!rankings) return null;
        const map = new Map<string, { score: number; tokens: number }>();
        for (const r of rankings) {
            if (r.runner?.id && r.score != null) {
                map.set(r.runner.id, { score: r.score, tokens: r.tokens ?? 1 });
            }
        }
        return map.size > 0 ? map : null;
    }, [betData]);

    // Expected rank per gender (by effectiveIndex desc)
    const expectedRankMap = useMemo(() => {
        const map = new Map<string, number>();
        const byGender = new Map<string, RaceRunner[]>();
        for (const r of runners ?? []) {
            const g = r.gender ?? 'ALL';
            const arr = byGender.get(g) ?? [];
            arr.push(r);
            byGender.set(g, arr);
        }
        for (const group of byGender.values()) {
            const sorted = [...group].sort((a, b) => (b.effectiveIndex || 0) - (a.effectiveIndex || 0));
            sorted.forEach((r, i) => { if (r.id) map.set(r.id, i + 1); });
        }
        return map;
    }, [runners]);

    const filteredRunners = useMemo(() => {
        let list = [...(runners ?? [])];
        if (genderFilter !== 'ALL') list = list.filter(r => r.gender === genderFilter);
        if (myBetsOnly && userBetRankings) list = list.filter(r => r.id && userBetRankings.has(r.id));
        if (searchTerm.trim()) {
            const term = searchTerm.trim().toLowerCase();
            list = list.filter(r => r.name?.toLowerCase().includes(term));
        }

        const isClosed = race?.status === RaceStatus.Ended || race?.status === RaceStatus.Archived;
        const tiebreak = (a: RaceRunner, b: RaceRunner) => (b.effectiveIndex || 0) - (a.effectiveIndex || 0);

        if (isClosed) {
            // ENDED/ARCHIVED: by result position, then DNF (0), then DNS (-1), then unranked
            const preferScratch = genderFilter === 'ALL';
            const rankField = (preferScratch && list.some(r => r.scratchResult != null)) ? 'scratchResult' : 'result' as const;
            list.sort((a, b) => {
                const ra = a[rankField];
                const rb = b[rankField];
                // Both null (no result): tiebreak
                if (ra == null && rb == null) return tiebreak(a, b);
                if (ra == null) return 1;
                if (rb == null) return -1;
                // Both DNS (-1): tiebreak
                if (ra === -1 && rb === -1) return tiebreak(a, b);
                // DNS after DNF
                if (ra === -1) return 1;
                if (rb === -1) return -1;
                // Both DNF (0): tiebreak
                if (ra === 0 && rb === 0) return tiebreak(a, b);
                // DNF after ranked
                if (ra === 0) return 1;
                if (rb === 0) return -1;
                // Both ranked: by position
                return ra - rb;
            });
        } else {
            // OPEN without bet: by effectiveIndex desc
            list.sort(tiebreak);
        }

        return list;
    }, [runners, race?.status, genderFilter, searchTerm, myBetsOnly, userBetRankings]);

    if (loading) return <RaceDetailsSkeleton />;
    if (error) return <div className="p-8"><ErrorBanner message={tc('error.generic')} /></div>;

    const totalRunnerCount = runners?.length ?? 0;
    const isFiltered = searchTerm !== '' || genderFilter !== 'ALL' || myBetsOnly;

    const deadline = race?.bettingDeadline ? new Date(race.bettingDeadline) : null;
    const hoursLeft = deadline ? (deadline.getTime() - Date.now()) / (1000 * 60 * 60) : 0;
    const isBettingOpen = hoursLeft > 0 && race?.status === 'OPEN';
    const isEnded = race?.status === 'ENDED';
    const isArchived = race?.status === 'ARCHIVED';

    const isLiveAvailable = race ? isRaceLive(race) : false;

    const isAdmin = currentUser?.roles?.includes(Role.Admin);
    const isCreator = race?.createdBy?.id === currentUser?.id;
    const canManage = isAdmin || isCreator;
    const creatorLabel = isCreator ? t('details.me') : (race?.createdBy?.username || t('details.unknown'));

    const confirmDelete = async () => {
        if (!runnerToDelete) return;
        setIsDeleting(true);
        try {
            await deleteRunner({ variables: { id: runnerToDelete.id! } });
            setRunnerToDelete(null);
        } catch (e) {
            console.error(e);
            toast(t('details.deleteRunner.error'), 'error');
        } finally {
            setIsDeleting(false);
        }
    };

    const confirmDeleteRace = async () => {
        setIsDeletingRace(true);
        try {
            await deleteRace({ variables: { id: id! } });
            toast(t('details.deleteRaceModal.success'), 'success');
            navigate('/races');
        } catch {
            toast(t('details.deleteRaceModal.error'), 'error');
        } finally {
            setIsDeletingRace(false);
        }
    };

    const confirmArchive = async () => {
        try {
            await updateRace({
                variables: { id: id!, input: { status: RaceStatus.Archived } },
                refetchQueries: ['GetRaceDetails'],
            });
            setShowArchiveModal(false);
            toast(t('details.archiveSuccess'), 'success');
        } catch {
            toast(t('details.archiveError'), 'error');
        }
    };

    // Status badge label + style
    const isRacing = race ? checkIsRacing(race) : false;
    const statusLabel = isBettingOpen ? tc('status.open')
        : isRacing ? tc('status.racing')
        : isEnded ? tc('status.ended')
        : isArchived ? tc('status.archived')
        : tc('status.closed');
    const statusStyle = isBettingOpen ? "bg-emerald-50 text-emerald-700"
        : isRacing ? "bg-rose-50 text-rose-700"
        : isEnded ? "bg-amber-50 text-amber-700"
        : isArchived ? "bg-slate-200 text-slate-600"
        : "bg-slate-100 text-slate-500";

    const isPrivate = race?.visibility === 'PRIVATE';

    const confirmToggleOfficial = async () => {
        const newValue = !race?.isOfficial;
        try {
            await setRaceOfficial({
                variables: { input: { raceId: id!, isOfficial: newValue } },
            });
            setShowOfficialModal(false);
            toast(
                newValue ? t('details.makeOfficialSuccess') : t('details.removeOfficialSuccess'),
                'success',
            );
        } catch {
            toast(tc('error.generic'), 'error');
        }
    };

    return (
        <Page>
            <PageHeader
                title={
                    <div className="flex items-center gap-2 min-w-0">
                        <span className="truncate text-lg font-bold text-slate-900 tracking-tight">{race?.name}</span>
                        {race?.isOfficial && <BadgeCheck size={18} className="text-amber-500 flex-shrink-0" />}
                        <div className={cn("px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide flex-shrink-0", statusStyle)}>
                            {statusLabel}
                        </div>
                    </div>
                }
                onBack={() => navigate('/races')}
                width="standard"
                actions={canManage && (
                    <AdminMenu
                        raceId={id!}
                        isOpen={isMenuOpen}
                        onToggle={() => setIsMenuOpen(!isMenuOpen)}
                        onClose={() => setIsMenuOpen(false)}
                        isArchived={!!isArchived}
                        isEnded={!!(isEnded || isArchived)}
                        isRaceOpen={race?.status === 'OPEN'}
                        inviteCode={race?.inviteCode}
                        isOfficial={!!race?.isOfficial}
                        isPrivate={isPrivate}
                        isAdmin={!!isAdmin}
                        isByUtmb={!!race?.isByUtmb}
                        isAutoSyncAvailable={!!race?.isAutoSyncAvailable}
                        onArchive={() => setShowArchiveModal(true)}
                        onDelete={() => setShowDeleteModal(true)}
                        onToggleOfficial={() => setShowOfficialModal(true)}
                        onSyncRunners={() => setShowSyncModal(true)}
                        onSyncResults={() => setShowSyncResultsModal(true)}
                    />
                )}
            />

            <PageContent width="standard">
                <div className="flex flex-col gap-6 md:grid md:grid-cols-[1fr_300px] md:gap-6">
                    {/* Info card — row 1 col 1 */}
                    <div className="md:col-start-1 md:row-start-1 space-y-4">
                        {race?.isOfficial && race?.pictureUri && showRaceImage && (
                            <div className="rounded-2xl bg-slate-100 h-40 md:h-52 flex items-center justify-center p-4 overflow-hidden">
                                <img
                                    src={race.pictureUri}
                                    alt={race.name ?? ''}
                                    className="max-h-full max-w-full object-contain"
                                    onError={() => setShowRaceImage(false)}
                                />
                            </div>
                        )}
                        <RaceInfoCard
                            creatorLabel={creatorLabel}
                            runnerCount={race?.runners?.length || 0}
                            deadline={deadline}
                            isBettingOpen={isBettingOpen}
                            raceFormat={race?.raceFormat}
                            distance={race?.distance}
                            elevationGain={race?.elevationGain}
                            startDate={race?.startDate ? new Date(race.startDate) : null}
                            isPrivate={isPrivate}
                            isOfficial={race?.isOfficial}
                        />
                    </div>

                    {/* Sidebar — col 2 on desktop, between info and tabs on mobile */}
                    <div className="md:col-start-2 md:row-start-1 md:row-end-4 md:sticky md:top-[88px] md:self-start space-y-4">
                        {isLiveAvailable && (
                            <LiveLeaderboardWidget raceId={id!} />
                        )}
                        {race?.description && <RaceDescription description={race.description} />}
                        {isPrivate && (
                            <ParticipantsCard
                                participants={race?.invitedUsers?.filter((u): u is NonNullable<typeof u> => u != null) ?? []}
                                creator={race?.createdBy ? { id: race.createdBy.id, username: race.createdBy.username, pictureUrl: race.createdBy.pictureUrl } : null}
                                canManage={!!canManage}
                                raceId={id!}
                            />
                        )}
                        {isBettingOpen && <BettingActivityCard raceId={id!} />}
                        {(isEnded || isArchived) && <LeaderboardPreview raceId={id!} />}
                    </div>

                    {/* Filters + Runners — row 2 col 1 */}
                    <div className="md:col-start-1 space-y-6 min-w-0">
                        {isLiveAvailable ? (
                            <LiveRunnerList
                                raceId={id!}
                                utmbLiveUrl={race?.utmbLiveUrl}
                                staticRunners={runners?.filter((r): r is NonNullable<typeof r> => r != null)}
                                raceFormat={race?.raceFormat}
                            />
                        ) : (
                            <>
                                <RunnerFilterBar
                                    searchTerm={searchTerm}
                                    onSearchChange={setSearchTerm}
                                    genderFilter={genderFilter}
                                    onGenderChange={setGenderFilter}
                                    showMyBetsFilter={userBetRankings != null}
                                    myBetsOnly={myBetsOnly}
                                    onMyBetsChange={setMyBetsOnly}
                                    filteredCount={filteredRunners.length}
                                    totalCount={totalRunnerCount}
                                />

                                <RunnerList
                                    runners={filteredRunners}
                                    canManage={!!canManage && race?.status === 'OPEN'}
                                    raceFormat={race?.raceFormat}
                                    onDeleteRunner={setRunnerToDelete}
                                    isFiltered={isFiltered}
                                    expectedRankMap={expectedRankMap}
                                    userBetRankings={userBetRankings}
                                    userBetScores={race?.status === RaceStatus.Ended || race?.status === RaceStatus.Archived ? userBetScores : null}
                                    useScratchResult={genderFilter === 'ALL'}
                                    badgeMode={
                                        race?.status === RaceStatus.Ended || race?.status === RaceStatus.Archived
                                            ? 'finalResult'
                                            : userBetRankings
                                                ? 'betPosition'
                                                : 'expectedRank'
                                    }
                                />
                            </>
                        )}
                    </div>
                </div>
            </PageContent>

            {/* Sticky CTA */}
            {totalRunnerCount > 0 && (isBettingOpen || isEnded) && (
                <div className="sticky bottom-0 z-30 pb-6 pb-safe">
                    <div className="absolute inset-x-0 bottom-0 h-full bg-gradient-to-t from-white via-white/95 to-transparent pointer-events-none" />
                    <div className="relative max-w-md mx-auto px-4 pt-6">
                        {isBettingOpen ? (
                            <button
                                className="w-full bg-emerald-600 text-white shadow-xl shadow-emerald-200 h-14 rounded-2xl font-bold text-lg flex items-center justify-center gap-3 active:scale-95 transition-transform hover:bg-emerald-700"
                                onClick={() => navigate(`/races/${id}/bet`)}
                            >
                                <div className="bg-emerald-500/30 p-1.5 rounded-lg">
                                    <Ticket size={20} className="text-white" />
                                </div>
                                <span>{race?.hasCurrentUserBet ? t('bets:editBet') : t('bets:placeBet')}</span>
                            </button>
                        ) : isEnded ? (
                            <button
                                className="w-full bg-amber-500 text-white shadow-xl shadow-amber-200 h-14 rounded-2xl font-bold text-lg flex items-center justify-center gap-3 active:scale-95 transition-transform hover:bg-amber-600"
                                onClick={() => navigate(`/races/${id}/recap`)}
                            >
                                <div className="bg-amber-400/30 p-1.5 rounded-lg">
                                    <Trophy size={20} className="text-white" />
                                </div>
                                <span>{t('recap.title')}</span>
                            </button>
                        ) : null}
                    </div>
                </div>
            )}

            {/* Delete runner modal */}
            {runnerToDelete && (
                <DeleteRunnerModal
                    runnerName={runnerToDelete.name}
                    isDeleting={isDeleting}
                    onConfirm={confirmDelete}
                    onCancel={() => setRunnerToDelete(null)}
                />
            )}

            {/* Delete race modal */}
            {showDeleteModal && (
                <DeleteRaceModal
                    raceName={race?.name || ''}
                    runnerCount={race?.runners?.length || 0}
                    isDeleting={isDeletingRace}
                    onConfirm={confirmDeleteRace}
                    onCancel={() => setShowDeleteModal(false)}
                />
            )}

            {/* Toggle official modal */}
            {showOfficialModal && (
                <ConfirmModal
                    icon={<BadgeCheck size={24} />}
                    iconClassName={race?.isOfficial ? "bg-slate-100 text-slate-600" : "bg-amber-50 text-amber-600"}
                    title={race?.isOfficial ? t('details.removeOfficial') : t('details.makeOfficial')}
                    message={race?.isOfficial ? t('details.removeOfficialConfirm') : t('details.makeOfficialConfirm')}
                    confirmLabel={tc('btn.confirm')}
                    isLoading={togglingOfficial}
                    onConfirm={confirmToggleOfficial}
                    onCancel={() => setShowOfficialModal(false)}
                />
            )}

            {/* Sync UTMB runners modal */}
            {showSyncModal && (
                <SyncUtmbRunnersModal
                    raceId={id!}
                    defaultLiveUrl={data?.race?.utmbLiveUrl ?? undefined}
                    onClose={() => setShowSyncModal(false)}
                    onSuccess={() => refetch()}
                    toast={toast}
                />
            )}

            {/* Sync UTMB results modal */}
            {showSyncResultsModal && (
                <SyncUtmbResultsModal
                    raceId={id!}
                    onClose={() => setShowSyncResultsModal(false)}
                    onSuccess={() => {
                        refetch();
                        navigate(`/races/${id}/results`);
                    }}
                    toast={toast}
                />
            )}

            {/* Archive race modal */}
            {showArchiveModal && (
                <ConfirmModal
                    icon={<Archive size={24} />}
                    iconClassName="bg-slate-100 text-slate-600"
                    title={t('details.archiveRace')}
                    message={t('details.archiveConfirm')}
                    confirmLabel={tc('btn.confirm')}
                    isLoading={archiving}
                    onConfirm={confirmArchive}
                    onCancel={() => setShowArchiveModal(false)}
                />
            )}
        </Page>
    );
}
